/**
 * SpendCapBanner Component (BL-37669b48)
 * AIDLC Administrative Backend
 *
 * Shown across the portal when agent spend is at 80% of today's or this month's cap,
 * or has reached it (new agent sessions are then refused). Offers the raise for that
 * period, which applies until the period ends. Refreshed every minute and whenever a
 * token record, a threshold or a raise arrives over the socket.
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import { fetchSpendCap, raiseSpendCap, type CapPeriod, type PeriodCapState, type SpendCapState } from '../../services/spendCapService';

const REFRESH_INTERVAL_MS = 60000;
const SOCKET_EVENTS = ['token:record', 'spend-cap:threshold', 'spend-cap:raised'] as const;
const PERIOD_WORDS: Record<CapPeriod, { label: string; raise: string }> = {
  day: { label: "Today's", raise: "today's" },
  month: { label: "This month's", raise: "this month's" },
};

const dollars = (usd: number): string => `$${usd.toFixed(2)}`;
const capDollars = (usd: number): string => (Number.isInteger(usd) ? `$${usd}` : dollars(usd));

/** The period the banner is about: a reached one before a warning, the day before the month. */
function periodToShow(state: SpendCapState): PeriodCapState | null {
  for (const level of ['reached', 'warning'] as const) {
    const match = [state.day, state.month].find((p) => p.level === level);
    if (match) return match;
  }
  return null;
}

function errorText(err: unknown): string {
  const fromServer = (err as { response?: { data?: { error?: string; details?: string[] } } })?.response?.data;
  return fromServer?.details?.join('; ') || fromServer?.error || (err as Error)?.message || 'The cap could not be raised';
}

interface RaiseDialogProps {
  period: PeriodCapState;
  maxUsd: number;
  onClose: () => void;
  onRaised: (state: SpendCapState) => void;
}

function RaiseCapDialog({ period, maxUsd, onClose, onRaised }: RaiseDialogProps) {
  const words = PERIOD_WORDS[period.period];
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const capUsd = Number(amount);
    if (!Number.isFinite(capUsd) || capUsd <= period.capUsd || capUsd > maxUsd) {
      setError(`Enter an amount above ${capDollars(period.capUsd)} and no more than ${capDollars(maxUsd)}.`);
      return;
    }
    if (!reason.trim()) {
      setError('Say why the cap is being raised.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onRaised(await raiseSpendCap(period.period, capUsd, reason.trim()));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" role="dialog" aria-labelledby="raise-cap-title">
        <h3 id="raise-cap-title" className="text-lg font-bold text-ldaja-black">Raise {words.raise} cap</h3>
        <p className="text-sm text-gray-600">
          {words.label} agent spend is {dollars(period.spentUsd)} of {capDollars(period.capUsd)}. The new cap applies until the {period.period} ends.
        </p>
        <label className="block text-sm font-medium text-gray-700">
          New cap (USD)
          <input
            type="number"
            min={period.capUsd}
            max={maxUsd}
            step="0.01"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError(null);
            }}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            autoFocus
          />
        </label>
        <label className="block text-sm font-medium text-gray-700">
          Reason
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </label>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-md bg-ldaja-black text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Raising…' : 'Raise cap'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function SpendCapBanner({ socket }: { socket: Socket | null }) {
  const [state, setState] = useState<SpendCapState | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await fetchSpendCap());
    } catch (err) {
      // No banner without figures; the next refresh tries again.
      console.error('Failed to load the agent spend cap:', err);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!socket) return;
    const refresh = (): void => void load();
    for (const event of SOCKET_EVENTS) socket.on(event, refresh);
    return () => {
      for (const event of SOCKET_EVENTS) socket.off(event, refresh);
    };
  }, [socket, load]);

  const shown = state ? periodToShow(state) : null;
  if (!state || !shown) return null;

  const words = PERIOD_WORDS[shown.period];
  const reached = shown.level === 'reached';
  const tone = reached ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800';
  const text = reached
    ? `${words.label} agent spend cap reached (${dollars(shown.spentUsd)} of ${capDollars(shown.capUsd)}). New agent sessions are paused.`
    : `${words.label} agent spend ${dollars(shown.spentUsd)} of ${capDollars(shown.capUsd)} (${shown.pct}%).`;

  return (
    <>
      <div className={`mb-4 flex items-center justify-between gap-4 rounded-lg border px-4 py-2 text-sm ${tone}`} role="status">
        <span>{text}</span>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="shrink-0 rounded-md border border-current px-3 py-1 text-xs font-medium hover:bg-white/60"
        >
          Raise {words.raise} cap
        </button>
      </div>
      {dialogOpen && (
        <RaiseCapDialog
          period={shown}
          maxUsd={state.sanityMaxUsd[shown.period]}
          onClose={() => setDialogOpen(false)}
          onRaised={(next) => {
            setState(next);
            setDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
